import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Grid2,
  CircularProgress,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import classNames from 'classnames';
import { PaymentFrequency } from '../../__generated__/types';
import { SubscriptionCard } from '../subscription-card';
import { PlaceholderCard } from './placeholder-card';
import { useHighLevelContext } from '../../hooks/HighLevelContext';
import { MembershipsListPillButtons } from './pill-buttons';
import {
  getDesignerSmartGuildBillingSplit,
  getDesignerSmartGuildDisplayedFeatures,
} from '../../utils/designer-smart-guild-pricing';
import { 
  getConfigurationsByFrequency, 
  getConfigurationsByAudience,
  getPlanConfiguration,
  shouldShowPlan,
  shouldShowPlaceholder,
  PlanConfiguration 
} from './plan-configuration';

interface EnhancedMembershipsListProps {
  selectedPlan?: string | null;
  onSelect?: (planId?: string) => void;
  showBetaPlans?: boolean;
  defaultFrequency?: PaymentFrequency;
  variant?: 'designer' | 'homeowner';
  showFrequencyToggle?: boolean;
  showPlaceholders?: boolean; // New prop to control placeholder display
  onAddPlan?: (tier: string, frequency: PaymentFrequency) => void; // Callback for adding plans
  plans?: any[]; // Optional prop to pass plans directly
  additionalCards?: React.ReactNode[];
  showApplyNowButton?: boolean;
  applyNowLabel?: string;
  applyNowTo?: string;
  cardClassName?: string;
  selectableCards?: boolean;
}

export const EnhancedMembershipsList: React.FC<EnhancedMembershipsListProps> = ({
  selectedPlan,
  onSelect,
  showBetaPlans = true,
  defaultFrequency = PaymentFrequency.BiAnnually,
  variant = 'designer',
  showFrequencyToggle = true,
  showPlaceholders = true,
  onAddPlan,
  plans: propPlans,
  additionalCards,
  showApplyNowButton = false,
  applyNowLabel = 'APPLY NOW',
  applyNowTo,
  cardClassName,
  selectableCards,
}) => {
  const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const [plans, setPlans] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentFrequency, setPaymentFrequency] = useState<PaymentFrequency>(defaultFrequency);
  
  const { getPlanList, client, error: highlevelError } = useHighLevelContext();

  // Fetch plans from HighLevel
  const loadPlans = useCallback(async () => {
    try {
      setIsLoading(true);

      const list = await getPlanList();

      const planItems = list.items || [];
      setPlans(planItems);
    } catch (error) {
      // Error fetching plans
    } finally {
      setIsLoading(false);
    }
  }, [getPlanList]);

  useEffect(() => {
    if (propPlans && propPlans.length > 0) {
      // Use plans passed as props
      setPlans(propPlans);
      setIsLoading(false);
    } else {
      // Fetch plans from HighLevel
      loadPlans();
    }
  }, [propPlans, loadPlans]);

  useEffect(() => {
    const handler = () => {
      loadPlans();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('highlevel:cacheCleared', handler);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('highlevel:cacheCleared', handler);
      }
    };
  }, [loadPlans]);

  // Get configurations based on variant
  const configurations = useMemo(() => {
    if (variant === 'homeowner') {
      return getConfigurationsByAudience('homeowner');
    }

    const frequencyConfigurations = getConfigurationsByFrequency(
      paymentFrequency,
      'designer'
    );

    if (frequencyConfigurations.length > 0) {
      return frequencyConfigurations;
    }

    return getConfigurationsByAudience('designer');
  }, [variant, paymentFrequency]);

  // Create plan map for quick lookup
  const planMap = useMemo(() => {
    const normalizeFrequency = (value: string) => {
      const normalized = value?.toLowerCase();

      switch (normalized) {
        case 'biannually':
        case 'bi-annually':
          return PaymentFrequency.BiAnnually;
        case 'annually':
          return PaymentFrequency.Annually;
        case 'monthly':
        default:
          return PaymentFrequency.Monthly;
      }
    };

    const map: Record<string, any> = {};

    plans.forEach((plan) => {
      const audience = String(plan?.metadata?.audience || '').toLowerCase();
      if (variant === 'designer' && audience && audience !== 'designer') {
        return;
      }
      if (variant === 'homeowner' && audience && audience !== 'homeowner') {
        return;
      }

      const tier = plan.metadata?.tier;
      const frequency = plan.pricing?.period;

      if (!tier || !frequency) {
        return;
      }

      const normalizedFrequency = normalizeFrequency(frequency);

      const key1 = `${tier}-${frequency}`;
      const key2 = `${tier}-${normalizedFrequency}`;
      
      map[key1] = plan;
      map[key2] = plan;
    });

    return map;
  }, [plans, variant]);

  // Convert frequency string to PaymentFrequency enum
  const getPaymentFrequencyEnum = (period: string): PaymentFrequency => {
    switch (period) {
      case 'monthly':
        return PaymentFrequency.Monthly;
      case 'annually':
        return PaymentFrequency.Annually;
      case 'bi-annually':
        return PaymentFrequency.BiAnnually;
      default:
        return PaymentFrequency.Monthly;
    }
  };

  // Get plan for a specific configuration
  const getPlanForConfiguration = (config: PlanConfiguration) => {
    // Convert PaymentFrequency enum to string for lookup
    const frequencyString = config.frequency === PaymentFrequency.BiAnnually ? 'bi-annually' :
                           config.frequency === PaymentFrequency.Annually ? 'annually' :
                           'monthly';
    
    const key = `${config.tier}-${frequencyString}`;
    const plan = planMap[key];
    
    return plan;
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress size={64} sx={{ color: '#00A06D' }} />
      </Box>
    );
  }

  // Handle HighLevel API errors
  if (highlevelError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Unable to load membership plans
        </Typography>
        <Typography variant="body2">
          {highlevelError.message || 'There was an issue connecting to our pricing service. Please try again later or contact support.'}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Payment frequency selector */}
      {variant === 'designer' && showFrequencyToggle && (
        <MembershipsListPillButtons
          paymentFrequency={paymentFrequency}
          setPaymentFrequency={setPaymentFrequency}
        />
      )}

      {/* Plans Grid */}
      <Grid2 container columns={12} spacing={2} justifyContent="center">
        {configurations.map((config) => {
          const plan = getPlanForConfiguration(config);
          const hasRealPlan = plan && config.enabled;

          if (hasRealPlan) {
            // Show real plan
            const planPrice = plan.pricing?.price || 0;
            const designerSmartGuildSplit = variant === 'designer'
              ? getDesignerSmartGuildBillingSplit(plan)
              : null;
            const displayPrice = designerSmartGuildSplit
              ? designerSmartGuildSplit.totalAnnualFeeAmount
              : planPrice;
            const smartGuildFeeFeatures = designerSmartGuildSplit
              ? [
                  `Annual Marketing Fee: $${formatCurrency(designerSmartGuildSplit.annualFeeAmount)}`,
                  `Monthly Platform Fee: $${formatCurrency(designerSmartGuildSplit.monthlyFeeAmount)}`,
                  `Due Now: $${formatCurrency(designerSmartGuildSplit.dueNowAmount)}`,
                ]
              : [];
            const planFeatures = getDesignerSmartGuildDisplayedFeatures(
              plan,
              plan.benefits
            );
            const planName = plan.name?.replace('BETA:', '') || '';
            const planOriginalPrice = plan.pricing?.originalPrice || null;
            const planDescription = designerSmartGuildSplit
              ? undefined
              : plan.description;

            return (
              <Grid2
                className={classNames('plan-container')}
                size={{ xs: 12, sm: 6, md: 4 }}
                maxWidth={368}
                key={`${config.tier}-${config.frequency}`}
                sx={onSelect ? { cursor: 'pointer' } : {}}
                onClick={() => {
                  onSelect && onSelect(selectedPlan === plan._id ? undefined : plan._id);
                }}
              >
                <SubscriptionCard
                  title={planName}
                  features={planFeatures}
                  plainFeatures={smartGuildFeeFeatures}
                  description={planDescription}
                  paymentFrequency={config.frequency}
                  price={displayPrice}
                  selected={selectedPlan === plan._id}
                  originalPrice={planOriginalPrice}
                  isBeta={showBetaPlans}
                  selectable={typeof selectableCards === 'boolean' ? selectableCards : !!onSelect}
                  className={cardClassName}
                  actionButtonLabel={showApplyNowButton ? applyNowLabel : undefined}
                  actionButtonTo={showApplyNowButton ? applyNowTo : undefined}
                />
              </Grid2>
            );
          } else if (showPlaceholders) {
            // Show placeholder
            return (
              <Grid2
                className={classNames('plan-container', 'placeholder')}
                size={{ xs: 12, sm: 6, md: 4 }}
                maxWidth={368}
                key={`placeholder-${config.tier}-${config.frequency}`}
              >
                <PlaceholderCard
                  tier={config.tier}
                  frequency={config.frequency}
                  isComingSoon={config.comingSoon}
                  onAddPlan={onAddPlan}
                />
              </Grid2>
            );
          }

          return null;
        })}
        {(additionalCards ?? []).map((card, index) => (
          <Grid2
            className={classNames('plan-container')}
            size={{ xs: 12, sm: 6, md: 4 }}
            maxWidth={368}
            key={`additional-card-${index}`}
          >
            <Box sx={{ height: '100%' }}>{card}</Box>
          </Grid2>
        ))}
      </Grid2>

    </Box>
  );
};
